import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Check, Loader2, Users, Zap, Lock, ArrowLeft, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { auth, payments, getStoredUser, clearSession } from '../api';
import { brand } from '../mock';

const money = (n) => 'R ' + (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export default function Pay() {
  const nav = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState(getStoredUser());
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState(() => (new URLSearchParams(window.location.search).get('plan') === 'yearly' ? 'yearly' : 'monthly'));
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem('ad_token')) { nav('/login'); return; }
    (async () => {
      try {
        const [me, pls] = await Promise.all([
          auth.me().catch((err) => {
            // auth.me 403s when firm is locked — fall back to stored user
            if (err?.response?.status === 403) return getStoredUser();
            return null;
          }),
          payments.plans().catch(() => ({ plans: [] })),
        ]);
        if (me) setUser(me);
        if (pls.plans) setPlans(pls.plans);
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, []);

  const isOwner = (user?.firmRole === 'owner') || user?.isPlatformAdmin;
  const isLocked = user?.accessStatus === 'locked';
  const lockReason = user?.lockedReason;

  const chosen = useMemo(() => plans.find((p) => p.key === selected), [plans, selected]);

  const startPayment = async () => {
    if (!chosen) return;
    setPaying(true);
    try {
      const res = await payments.initialize(selected);
      // Hard redirect to Paystack hosted checkout
      window.location.href = res.authorization_url;
    } catch (err) {
      toast({
        title: 'Could not start payment',
        description: err?.response?.data?.detail || 'Try again in a moment.',
        variant: 'destructive',
      });
      setPaying(false);
    }
  };

  const logout = () => { clearSession(); nav('/login'); };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-stone-50">
        <div className="flex items-center gap-2 text-stone-600"><Loader2 className="animate-spin" size={18}/> Loading plans…</div>
      </div>
    );
  }

  if (!isOwner) {
    return (
      <div className="min-h-screen bg-stone-950 grid place-items-center p-6">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl">
          <div className="h-12 w-12 rounded-lg bg-amber-100 text-amber-800 grid place-items-center mb-4"><Lock size={20}/></div>
          <h1 className="font-serif text-2xl font-semibold text-stone-900">Only the firm owner can pay</h1>
          <p className="mt-2 text-sm text-stone-600">Ask the firm owner for {user?.firm || 'your firm'} to sign in and renew the subscription.</p>
          <Button onClick={logout} variant="outline" className="mt-6 w-full">Sign out</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100">
      <div className="max-w-5xl mx-auto px-4 lg:px-8 py-12">
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="pointer-events-none absolute -inset-1 rounded-lg bg-sky-500/30 blur-md"/>
              <div className="relative h-10 w-10 rounded-lg bg-black overflow-hidden grid place-items-center ring-1 ring-sky-500/40"><img src="/brand/accu-desk-logo.png" alt="Accu Desk" className="h-full w-full object-contain"/></div>
            </div>
            <div>
              <div className="font-semibold text-white text-sm">{brand.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-stone-500">{brand.tagline}</div>
            </div>
          </div>
          <button onClick={logout} className="text-xs text-stone-400 hover:text-white underline underline-offset-2">Sign out</button>
        </div>

        {isLocked && (
          <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-5 mb-8 flex items-start gap-4" data-testid="pay-locked-banner">
            <Lock size={20} className="mt-0.5 text-red-300 flex-shrink-0"/>
            <div>
              <div className="font-semibold text-red-200">Your account is locked</div>
              <div className="mt-1 text-sm text-red-100/90">{lockReason || 'Please subscribe to continue using Accu Desk.'}</div>
              <div className="mt-1.5 text-xs text-red-100/70">Pick a plan below — access unlocks automatically the moment payment clears.</div>
            </div>
          </div>
        )}

        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 text-sky-300 text-[11px] uppercase tracking-widest font-semibold"><Sparkles size={13}/> {user?.firm || 'Your firm'} · {chosen?.userCount || 1} seat{chosen?.userCount === 1 ? '' : 's'}</div>
          <h1 className="mt-2 font-serif text-4xl lg:text-5xl font-semibold text-white">Pick your plan</h1>
          <p className="mt-3 text-stone-400 max-w-xl mx-auto text-sm">All features. One flat price. Add teammates on the Team page and your next renewal grows with you.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {plans.map((p) => {
            const isPicked = p.key === selected;
            const isYearly = p.key === 'yearly';
            const monthlyPlan = plans.find((x) => x.key === 'monthly');
            const yearlyEquivPerMonth = isYearly && monthlyPlan ? (p.amount / 12) : null;
            const savings = isYearly && monthlyPlan ? ((monthlyPlan.amount * 12) - p.amount) : 0;
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => setSelected(p.key)}
                className={`relative text-left rounded-2xl p-6 lg:p-8 transition-all ${isPicked ? 'bg-white text-stone-900 ring-2 ring-emerald-400 shadow-2xl' : 'bg-stone-900/60 text-stone-200 ring-1 ring-stone-800 hover:ring-stone-700'}`}
                data-testid={`plan-${p.key}`}
              >
                {isYearly && (
                  <div className="absolute -top-3 right-6 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-wider shadow">
                    <Zap size={10}/> Save {money(savings)}
                  </div>
                )}
                <div className="flex items-start justify-between">
                  <div>
                    <div className={`text-xs uppercase tracking-wider font-semibold ${isPicked ? 'text-emerald-700' : 'text-sky-400'}`}>{p.label} plan</div>
                    <div className="mt-2 flex items-end gap-1.5">
                      <span className="font-serif text-5xl font-semibold">{money(p.amount)}</span>
                      <span className={`text-sm mb-1.5 ${isPicked ? 'text-stone-600' : 'text-stone-400'}`}>/ {isYearly ? 'year' : 'month'}</span>
                    </div>
                    {yearlyEquivPerMonth && (
                      <div className={`text-xs mt-1 ${isPicked ? 'text-stone-500' : 'text-stone-500'}`}>= {money(yearlyEquivPerMonth)} / month</div>
                    )}
                  </div>
                  <div className={`h-6 w-6 rounded-full border-2 grid place-items-center ${isPicked ? 'border-emerald-500 bg-emerald-500' : 'border-stone-600'}`}>
                    {isPicked && <Check size={13} className="text-white"/>}
                  </div>
                </div>
                <div className={`mt-6 pt-5 border-t text-sm space-y-2 ${isPicked ? 'border-stone-200 text-stone-700' : 'border-stone-800 text-stone-300'}`}>
                  <div className="flex items-center gap-2"><Users size={13} className={isPicked ? 'text-emerald-700' : 'text-sky-400'}/> {p.userCount} active seat{p.userCount === 1 ? '' : 's'} · {money(p.base)} base + {p.userCount > 1 ? `${p.userCount - 1} × ${money(p.addon)} per extra seat` : 'add seats on the Team page'}</div>
                  <div className="flex items-center gap-2"><Check size={13} className={isPicked ? 'text-emerald-700' : 'text-sky-400'}/> Compliance engine, client portal, chat, timesheets & reports</div>
                  <div className="flex items-center gap-2"><Check size={13} className={isPicked ? 'text-emerald-700' : 'text-sky-400'}/> Unlimited clients, tasks and templates</div>
                  <div className="flex items-center gap-2"><Check size={13} className={isPicked ? 'text-emerald-700' : 'text-sky-400'}/> Firm Financials with monthly P&amp;L and Excel export</div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-10 rounded-xl bg-stone-900/60 ring-1 ring-stone-800 p-6 flex items-center justify-between gap-4 flex-wrap">
          <div className="text-sm text-stone-300">
            Paying with Paystack · test mode. Use card <span className="font-mono text-xs text-sky-300">4084 0840 8408 4081</span>, any future expiry, any CVV.
          </div>
          <Button
            onClick={startPayment}
            disabled={paying || !chosen}
            className="bg-emerald-500 hover:bg-emerald-400 text-stone-950 font-semibold h-12 px-7 text-base"
            data-testid="pay-start-btn"
          >
            {paying ? <><Loader2 size={16} className="mr-2 animate-spin"/> Opening checkout…</> : <><CreditCard size={16} className="mr-2"/> Pay {money(chosen?.amount || 0)} now</>}
          </Button>
        </div>

        {!isLocked && (
          <div className="mt-6 text-center">
            <button onClick={() => nav('/dashboard')} className="text-xs text-stone-500 hover:text-stone-300 inline-flex items-center gap-1"><ArrowLeft size={11}/> Back to dashboard</button>
          </div>
        )}
      </div>
    </div>
  );
}
